import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Select, MenuItem, Button,
  Table, TableHead, TableRow, TableCell, TableBody, Alert
} from '@mui/material';

const ProgramPayment = ({ csrfToken, razorpayKey }) => {
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [programDetails, setProgramDetails] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', contact: '' });
  const [error, setError] = useState('');
  const [selectedFee, setSelectedFee] = useState(0);

  useEffect(() => {
    fetch("/formdata/programs/")
      .then(res => res.json())
      .then(setPrograms)
      .catch(() => alert("Failed to load programs."));
  }, []);

  const handleProgramChange = (event) => {
    const programId = event.target.value;
    setSelectedProgram(programId);
    if (!programId) return;

    fetch(`/formdata/get_program_fee/${programId}/`)
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          setError(data.error);
          setProgramDetails(null);
        } else {
          setError('');
          setProgramDetails(data);
          setSelectedFee(parseFloat(data.total_per_person));
        }
      })
      .catch(() => alert("Error fetching program details."));
  };

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePayment = () => {
    const { name, email, contact } = form;
    if (!name || !email || !contact) {
      alert("Please fill in all participant details.");
      return;
    }

    fetch("/formdata/create_order/", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRFToken": csrfToken
      },
      body: `amount=${selectedFee}`
    })
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          alert("Error: " + data.error);
          return;
        }

        const options = {
          key: razorpayKey,
          amount: data.amount,
          currency: data.currency,
          name: "The Data Tech Labs",
          description: "Program Payment",
          order_id: data.order_id,
          handler: function (response) {
            fetch("/formdata/verify_payment/", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": csrfToken
              },
              body: JSON.stringify({
                ...response,
                program_id: selectedProgram,
                ...form
              })
            }).then(res => res.json())
              .then(res => alert(res.message));
          },
          prefill: { name, email, contact },
          theme: { color: "#0d6efd" }
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      })
      .catch(() => alert("Error creating Razorpay order."));
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto', bgcolor: '#fff', boxShadow: 3, borderRadius: 2 }}>
      <Typography variant="h5" align="center" gutterBottom>
        Secure Your Spot - Program Payment
      </Typography>

      <TextField fullWidth label="Full Name" name="name" value={form.name} onChange={handleInputChange} margin="normal" />
      <TextField fullWidth label="Email Address" name="email" type="email" value={form.email} onChange={handleInputChange} margin="normal" />
      <TextField fullWidth label="Mobile Number" name="contact" value={form.contact} onChange={handleInputChange} margin="normal" />

      <Select fullWidth value={selectedProgram || ''} onChange={handleProgramChange} displayEmpty sx={{ my: 2 }}>
        <MenuItem value="">-- Select a Program --</MenuItem>
        {programs.map(program => (
          <MenuItem key={program.id} value={program.id}>
            {program.program_name}
          </MenuItem>
        ))}
      </Select>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {programDetails && (
        <Table sx={{ mt: 2 }}>
          <TableHead>
            <TableRow>
              {["Program Name", "Duration", "Intake", "Mode", "Fees (₹)", "GST (₹)", "Total (₹)", "Timing", "Days"].map(header => (
                <TableCell key={header} align="center">{header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell align="center">{programDetails.program_name}</TableCell>
              <TableCell align="center">{programDetails.duration}</TableCell>
              <TableCell align="center">{programDetails.intake}</TableCell>
              <TableCell align="center">{programDetails.mode}</TableCell>
              <TableCell align="center">₹{parseFloat(programDetails.fees).toFixed(2)}</TableCell>
              <TableCell align="center">₹{parseFloat(programDetails.gst).toFixed(2)}</TableCell>
              <TableCell align="center">₹{parseFloat(programDetails.total_per_person).toFixed(2)}</TableCell>
              <TableCell align="center">{programDetails.timing}</TableCell>
              <TableCell align="center">{programDetails.days}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}

      {programDetails && (
        <Button variant="contained" fullWidth sx={{ mt: 3 }} onClick={handlePayment}>
          Proceed to Payment
        </Button>
      )}
    </Box>
  );
};

export default ProgramPayment;
